import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Button, Divider, Grid, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { customRenderers } from '../../helpers';
import { AutomatedInsightSections } from '../../modules/automated-insights/constants';

import { TrackEvent } from '../analytics';

const styles = {
  button: {
    width: { xs: 170, sm: 'auto' },
    fontSize: '12px',
    borderRadius: '20px',
    textTransform: 'none',
  },
};

const Segment = ({
  segment,
  isOwner,
  hasSessionInsights,
  hasInsightsGenerating,
  onSelectSegment,
  onSectionChange,
  onInsightsRegeneration,
  onSelectSegmentToEdit,
  initialIsExpanded = false,
  showActions = true,
  canToggle = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(initialIsExpanded);
  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleExpand = () => {
    if (canToggle) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleViewFindingsClick = () => {
    onSelectSegment(segment);
    onSectionChange(AutomatedInsightSections.KeyFindings);
    handleClose();
  };

  const handleCustomizeAudienceClick = () => {
    onSelectSegmentToEdit(segment);
  };

  const handleGenerateFindingsClick = () => {
    onInsightsRegeneration({
      segment,
      trackEventName: TrackEvent.GENERATE_PULSE_FROM_CUSTOM_AUDIENCE_EXISTING_MY_AUDIENCES,
    });
    handleClose();
  };

  return (
    <Box
      sx={{
        paddingBottom: 2,
        backgroundColor: '#FFFFFF',
        borderRadius: '16px',
        border: '1px solid rgba(217, 217, 217, 0.83)',
      }}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="caption" component="div" sx={{ paddingX: 2, paddingY: 1, fontWeight: 500 }}>
            {segment?.name}
          </Typography>
        </Grid>
        <Grid item>
          {showActions && (
            <div>
              <IconButton
                id="options-button"
                aria-controls={isOpen ? 'button-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={isOpen ? 'true' : undefined}
                disableRipple
                onClick={handleOpen}>
                <MoreVertIcon />
              </IconButton>
              <Menu
                id="button-menu"
                anchorEl={anchorEl}
                open={isOpen}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'center',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  sx: {
                    boxShadow: 'none',
                    border: '1px solid rgba(160, 160, 160, 0.37)',
                    borderRadius: '11px',
                  },
                }}
                MenuListProps={{
                  'aria-labelledby': 'options-button',
                }}>
                {hasSessionInsights && (
                  <MenuItem onClick={handleViewFindingsClick} sx={{ fontSize: '12px' }}>
                    See findings
                  </MenuItem>
                )}
                {isOwner && !hasSessionInsights && !hasInsightsGenerating && (
                  <MenuItem onClick={handleGenerateFindingsClick} sx={{ fontSize: '12px' }}>
                    Generate findings
                  </MenuItem>
                )}
                {isOwner && (
                  <MenuItem onClick={handleCustomizeAudienceClick} sx={{ fontSize: '12px' }}>
                    Customize this persona
                  </MenuItem>
                )}
              </Menu>
            </div>
          )}
        </Grid>
      </Grid>
      <Divider />
      <Box
        onClick={handleToggleExpand}
        sx={{
          position: 'relative',
          paddingY: 1,
          fontSize: '12px',
          lineHeight: '20px',
          maxHeight: isExpanded ? 'auto' : 70,
          overflowY: isExpanded ? 'auto' : 'hidden',
          cursor: 'pointer',
        }}>
        <Box sx={{ maxHeight: isExpanded ? 300 : 70, paddingX: 2, overflowY: isExpanded ? 'auto' : 'hidden' }}>
          <>
            <ReactMarkdown components={customRenderers}>{segment?.description}</ReactMarkdown>
            {!isExpanded && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 40,
                  width: '100%',
                  background: 'linear-gradient(rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.9))',
                }}
              />
            )}
          </>
        </Box>
        {showActions && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              flexWrap: 'wrap',
              mt: 3,
              gap: 1,
              paddingX: 2,
            }}>
            {isOwner && (
              <Button
                onClick={handleCustomizeAudienceClick}
                sx={{
                  ...styles.button,
                  backgroundColor: 'FFFFFF',
                  color: '#778D86',
                  border: '1px solid #778D86',
                  '&.Mui-disabled': {
                    background: '#EAEAEA',
                    color: '#C0C0C0',
                    border: 'none',
                  },
                }}>
                Customize this persona
              </Button>
            )}
            {hasSessionInsights && (
              <Button
                onClick={handleViewFindingsClick}
                sx={{
                  ...styles.button,
                  backgroundColor: '#778D86',
                  color: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: '#778D86',
                  },
                  '&.Mui-disabled': {
                    background: '#EAEAEA',
                    color: '#C0C0C0',
                  },
                }}>
                See findings
              </Button>
            )}
            {isOwner && !hasSessionInsights && !hasInsightsGenerating && (
              <Button
                onClick={handleGenerateFindingsClick}
                sx={{
                  ...styles.button,
                  backgroundColor: '#778D86',
                  color: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: '#778D86',
                  },
                  '&.Mui-disabled': {
                    background: '#EAEAEA',
                    color: '#C0C0C0',
                  },
                }}>
                Generate Findings
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Segment;
